import React, { useState } from 'react';
import { Button, Flex, Text, CoveragesAccordion, Card, DescriptionItem, CheckIcon, DownloadIcon, TermsAndConditionsModal, OfferFilesModal, BrandLogo } from 'src/components';
import styled from '@emotion/styled';
import { DisplayDateFormat, formatPrice, getBrandLogo, i18n, resolvePremiums, StandardizedCoverage } from 'src/common';
import { colors } from 'src/theme';
import { CalculationCoverageDto, GetOfferDto } from 'src/common/api/apollo/types/types';
import dayjs from 'dayjs';

interface OfferCheckoutCardProps {
  offer: GetOfferDto;
  onSelectOffer?: () => void;
}

const OfferHeader = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 24px;
  @media (min-width: 400px) {
    gap: 0;
    flex-direction: row;
    justify-content: space-between;
  }
`;

const OfferDetails = styled.div`
  display: flex;
  background: ${() => `${colors.background6}70`};
  border-radius: 4px;
  padding: 8px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OfferTitleBox = styled.div`
  border-radius: 4px;
  padding: 4px;
  width: 100%;
`;

function mapCoverageToStandardized(cov: CalculationCoverageDto, isIncluded: boolean): StandardizedCoverage {
  return {
    code: cov.code,
    name: cov.name,
    description: cov.description ?? undefined,
    insuredAmount: cov.insuredAmount ?? undefined,
    premium: cov.premium ?? undefined,
    isIncluded,
    risks: cov.risks?.map((r) => ({
      code: r.code,
      name: r.name,
      description: r.description ?? undefined,
      insuredAmount: r.insuredAmount ?? undefined,
      premium: r.premium ?? undefined,
      claims: r.claims?.map((c) => ({
        name: c.name,
        insuredAmount: c.insuredAmount,
        premium: c.premium,
      })) ?? undefined,
    })) ?? undefined,
  } as StandardizedCoverage;
}

export const OfferCheckoutCard: React.FC<OfferCheckoutCardProps> = ({ offer, onSelectOffer }) => {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const { productTypeSpecific: pts } = offer;
  const brandCode = pts.brandId.code;
  const brandName = pts.brandId.name;
  const brandLogo = pts.brandId.logos?.dzo?.details;
  const calc = pts.calculationData;
  const { annual: totalPremium, monthly: monthlyPrice } = resolvePremiums(pts.payment);

  const coverageGroups: StandardizedCoverage[] = [];

  if (calc?.coverages) {
    const constructionLabel = i18n.t('components.cards.offerCard.coverageConstruction');
    const householdLabel = i18n.t('components.cards.offerCard.coverageHousehold');

    const constructionCov = calc.coverages.find(
      (c) => c.name === constructionLabel || c.name === 'Građevinski dio',
    );
    const householdCov = calc.coverages.find(
      (c) => c.name === householdLabel || c.name === 'Kućanstvo',
    );

    if (constructionCov) {
      coverageGroups.push(mapCoverageToStandardized(constructionCov, true));
    }
    if (householdCov) {
      coverageGroups.push(mapCoverageToStandardized(householdCov, true));
    }
  }

  if (calc?.additionalCoverages) {
    calc.additionalCoverages.forEach((ac) => {
      coverageGroups.push(mapCoverageToStandardized(ac, true));
    });
  }

  const handleDownloadTerms = () => {
    setTermsModalOpen(true);
  }

  return (
    <Card padding={8} style={{ paddingBottom: '16px' }}>
      <Flex vertical gap={8}>
        <Flex vertical gap={24}>
          <OfferHeader>
            <BrandLogo brandCode={brandCode} src={getBrandLogo(brandCode, brandLogo)} alt={brandName} />
          </OfferHeader>
          <Flex align="flex-end" justify="space-between" style={{ width: '100%' }}>
            <Flex vertical>
              <Text type="sMedium" color={colors.text1} bold>
                {i18n.t('components.cards.offerCard.policyDuration')}
              </Text>
              <Text type="small" color={colors.text1}>
                {`${
                  dayjs(pts.newPolicy?.[0].newPolicyStartDate).format(DisplayDateFormat)
                } - ${
                  pts.newPolicy?.[0].policyDuration === 99 ?
                    i18n.t('components.cards.offerCard.untilCancellation')
                      : dayjs(pts.newPolicy?.[0].newPolicyEndDate).format(DisplayDateFormat)
                }`}
              </Text>
            </Flex>
            <Flex vertical align="flex-end">
              <Flex align="baseline" justify="center">
                <Text type="large" color={colors.text1} bold style={{ fontFamily: 'Red Hat Text, sans-serif' }}>
                  {monthlyPrice.toFixed(2).replace('.', ',')} €
                </Text>
                <Text type="base" color={colors.text4}>
                  /{i18n.t('layout.headerData.perMonth')}
                </Text>
              </Flex>
              <Text type="tiny" color={colors.text4} style={{ textAlign: 'center' }}>
                ({i18n.t('components.cards.offerCard.total')} {totalPremium.toFixed(2).replace('.', ',')} €{' '}
                {i18n.t('components.cards.offerCard.annually')})
              </Text>
            </Flex>
          </Flex>
        </Flex>
        {false ? (
          <CoveragesAccordion
            coverageGroups={coverageGroups}
            selectedAdditionalCoverages={[]}
            handleToggleCalc={() => undefined}
            alwaysOpen={false}
          />
        ) : (
          <Flex vertical gap={4} >
            { pts?.building?.package?.productId && <OfferDetails>
              <CheckIcon color={colors.green1} width={20} height={20} />
              <OfferTitleBox>
                <DescriptionItem
                  label={i18n.t('components.cards.offerCard.coverageConstruction')}
                  value={(
                    <Flex vertical>
                      <Text type='small'>
                        {`${i18n.t('components.cards.offerCard.package')}: ${pts.building.package.label}`}
                      </Text>
                      { pts.building.quote && (
                        <Text type='small'>
                          {`${i18n.t('components.cards.offerCard.insuredSum')}: ${formatPrice(pts.building.quote as number)}` }
                        </Text>
                      )}
                    </Flex>
                  )}
                  labelSize="medium"
                  valueSize='small'
                  labelColor={colors.text1}
                  bold={true}
                />
              </OfferTitleBox>
            </OfferDetails> }
            { pts?.household?.package?.productId && <OfferDetails>
              <CheckIcon color={colors.green1} width={20} height={20} />
              <OfferTitleBox>
                <DescriptionItem
                  label={i18n.t('components.cards.offerCard.coverageHousehold')}
                  value={(
                    <Flex vertical>
                      <Text type='small'>
                        {`${i18n.t('components.cards.offerCard.package')}: ${pts.household.package.label}`}
                      </Text>
                      { pts.household.quote && (
                        <Text type='small'>
                          {`${i18n.t('components.cards.offerCard.insuredSum')}: ${formatPrice(pts.household.quote as number)}` }
                        </Text>
                      )}
                    </Flex>
                  )}
                  labelSize="medium"
                  valueSize='small'
                  labelColor={colors.text1}
                  bold={true}
                />
              </OfferTitleBox>
            </OfferDetails> }
            <Flex align="center" gap={8} style={{ padding: "0 8px" }}>
              <DownloadIcon/>
              <Button btnType="textPrimary" size="small" onClick={handleDownloadTerms}>
                {i18n.t('components.cards.offerPreviewCard.insuranceTerms')}
              </Button>
              <Button btnType="textPrimary" size="small" onClick={() => setFilesModalOpen(true)}>
                {i18n.t('components.modals.offerFilesModal.title')}
              </Button>
            </Flex>
          </Flex>
        )}
        <Button
          onClick={onSelectOffer}
          type="button"
          btnType="primary"
          size="extralarge"
          text={`${i18n.t('pages.offers.selectOffer')}`}
        />
      </Flex>
      <TermsAndConditionsModal
        open={termsModalOpen}
        offerId={offer._id}
        onClose={() => setTermsModalOpen(false)}
      />
      <OfferFilesModal
        open={filesModalOpen}
        offerId={offer._id}
        onClose={() => setFilesModalOpen(false)}
      />
    </Card>
  );
};
